import React, { useCallback, useEffect, useRef, useState } from "react";
import { useWaysToStyle } from "../../context/waysToStyle/WaysToStyleContext";
import WaysToStyleModalThumbnails from "./WaysToStyleModalThumbnails";
import Modal from "../modal/Modal";
import { getDefaultThumbnailIndex } from "./utils";
import {
  MODAL_TYPE,
  WAYS_TO_STYLE_OPEN_MODAL_EVENT,
  MODAL_CONTENT_LOAD_DELAY_MS,
} from "../../constants";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import useEventBus from "../../hooks/useEventBus";
import { StylingLookWithStockPrice } from "../../types/productTypes/stylingLookProduct";
import WaysToStyleModalCarousel from "./WaysToStyleModalCarousel";
import classnamesBind from "classnames/bind";
import WaysToStyleSimilarItems from "./WaysToStyleSimilarItems";
import { reorderThumbnailsBySelectedStylingLook } from "./utils";
import { ProductData } from "../../types/productTypes/product";
import WaysToStyleProductCard from "./WaysToStyleProductCard";
import styles from "./WaysToStyleModal.scss";

const classnames = classnamesBind.bind(styles);

interface WaysToStyleModalData {
  selectedLook: StylingLookWithStockPrice;
  lookIndex: number;
  thumbnailIndex: number;
}

const WaysToStyleModal: React.FC = () => {
  const { getTranslation, product } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    product?: ProductData;
  };
  const { resetStockPriceData, stockPriceByLookIndex } = useWaysToStyle();
  const stockPriceByLookIndexRef = useRef(stockPriceByLookIndex);
  stockPriceByLookIndexRef.current = stockPriceByLookIndex;
  const [open, setOpen] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [focusOnCloseElement, setFocusOnCloseElement] =
    useState<HTMLElement | null>(null);
  const [stylingLooksModalData, setStylingLooksModalData] =
    useState<WaysToStyleModalData | null>(null);
  const [modalId] = useState<string>(
    `${MODAL_TYPE.SIDE}${Math.floor(Math.random() * 10000)}`
  );
  const [selectedLookIndex, setSelectedLookIndex] = useState(0);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(1);
  const selectedLookIndexRef = useRef(selectedLookIndex);
  selectedLookIndexRef.current = selectedLookIndex;
  const contentLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modalHeaderHeight, setModalHeaderHeight] = useState(0);

  const clearContentLoadTimer = () => {
    if (contentLoadTimer.current) {
      clearTimeout(contentLoadTimer.current);
      contentLoadTimer.current = null;
    }
  };

  useEffect(() => clearContentLoadTimer, []);

  const handleLookIndexChange = useCallback((newLookIndex: number) => {
    if (newLookIndex === selectedLookIndexRef.current) {
      return;
    }

    setSelectedLookIndex(newLookIndex);
    const currentLook = stockPriceByLookIndexRef.current[newLookIndex];
    const defaultThumbnailIndex = getDefaultThumbnailIndex(
      currentLook?.products
    );
    setSelectedThumbnailIndex(defaultThumbnailIndex);
  }, []);

  useEventBus(
    WAYS_TO_STYLE_OPEN_MODAL_EVENT,
    ({
      focusOnCloseElement,
      data,
    }: {
      focusOnCloseElement: HTMLElement;
      data?: WaysToStyleModalData | null;
    }) => {
      setOpen(true);
      setFocusOnCloseElement(focusOnCloseElement);

      if (!data) {
        clearContentLoadTimer();
        setContentLoaded(false);
        setStylingLooksModalData(null);
        return;
      }
      setStylingLooksModalData(data);
      setSelectedLookIndex(data.lookIndex);
      setSelectedThumbnailIndex(data.thumbnailIndex);

      clearContentLoadTimer();
      contentLoadTimer.current = setTimeout(() => {
        setContentLoaded(true);
      }, MODAL_CONTENT_LOAD_DELAY_MS);
    },
    []
  );

  const renderContent = () => {
    if (!stylingLooksModalData) return null;

    const currentLook = stockPriceByLookIndex[selectedLookIndex];

    if (!currentLook) return null;

    const orderedThumbnails = reorderThumbnailsBySelectedStylingLook(
      currentLook.products,
      product?.id
    );
    const selectedThumbnailProduct =
      orderedThumbnails[selectedThumbnailIndex] ?? orderedThumbnails[0];

    return (
      <div className={classnames({ isLoading: !contentLoaded })}>
        <WaysToStyleModalCarousel
          selectedLookIndex={selectedLookIndex}
          onSelectedLookIndexChange={handleLookIndexChange}
          isOpen={open}
        />
        <div
          className={classnames(styles.thumbnailContainer, styles.sticky)}
          style={{ top: modalHeaderHeight }}
          data-testid="modalThumbnailContainer"
        >
          <WaysToStyleModalThumbnails
            products={orderedThumbnails}
            selectedThumbnailIndex={selectedThumbnailIndex}
            onThumbnailSelect={setSelectedThumbnailIndex}
          />
        </div>
        {selectedThumbnailProduct ? (
          <>
            <div className={styles.productCardContainer}>
              <WaysToStyleProductCard
                product={selectedThumbnailProduct}
                lookProducts={currentLook.products}
              />
            </div>
            <WaysToStyleSimilarItems
              selectedProductId={selectedThumbnailProduct?.id}
            />
          </>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      title={getTranslation("pdp_associated_buy_the_look_title")}
      open={open}
      focusOnClose={focusOnCloseElement}
      modalId={modalId}
      modalType={MODAL_TYPE.SIDE}
      forceDOMRewrite={true}
      closeModal={() => {
        clearContentLoadTimer();
        setOpen(false);
        setContentLoaded(false);
        resetStockPriceData();
      }}
      contentLoaded={contentLoaded}
      additionalClasses={styles.uppercaseTitle}
      onHeaderHeightChange={setModalHeaderHeight}
    >
      {renderContent()}
    </Modal>
  );
};

export default WaysToStyleModal;
